import type { NotificationAdapter, NoticePage, NoticeRequest, NoticeSummary } from '@beyond-js/ui/react';
import { ApiProblem, type Api } from '@cdn/api';
import { Availability } from './availability';

interface ISource {
	product: string;
	state: 'available' | 'unavailable';
}

interface IItem {
	id: string;
	product: string;
	kind?: string;
	title: string;
	summary?: string;
	occurred: string;
	read?: string;
	group?: string;
}

/**
 * The shared notification components' adapter over CDN's relay (`/v1/notifications…`, protocol
 * `beyond-notifications/1`). The service forwards each call to Projects with the person's own session, so
 * this page never holds a credential and never names an account.
 *
 * When there is no inbox to read — this installation does not use Projects, or Projects does not answer — the
 * relay answers its two reads `{available: false, reason}` and the components show notifications as
 * unavailable with that reason (the family's sentence since `@beyond-js/ui` 0.7.6), never an empty inbox; `Availability` keeps the page from asking again (never after
 * `not_configured`, only after its retry delay after `projects_unavailable`). A refusal `UNAVAILABLE` (503) or
 * no answer at all is the same unavailable state. Projects' `more` and `sources` reach the components as they
 * are: a bounded count reads "N+", and a source whose state is `unavailable` is a product whose items are
 * hidden, named by its display name as a partial result.
 */
export class RelayAdapter implements NotificationAdapter {
	#api: Api;
	#locale: () => string;
	#availability = new Availability();

	/**
	 * @param locale The language the producers render titles in, read at every call
	 */
	constructor(api: Api, locale: () => string) {
		this.#api = api;
		this.#locale = locale;
	}

	/** Why the inbox was last unavailable, or null while it is readable */
	get reason(): string | null {
		return this.#availability.reason;
	}

	async summary(): Promise<NoticeSummary> {
		if (this.#availability.held(true)) return { available: false, unread: null, reason: this.reason };
		const answer = await this.#call<{ unread: number; more: boolean; sources?: ISource[] }>('GET', `/v1/notifications/summary?${this.#query({})}`);
		if (!answer) return { available: false, unread: null };
		if (this.#availability.note(answer)) return { available: false, unread: null, reason: this.reason };
		// Projects' bound (`more`, shown "N+") and each product's reach (`sources`) pass through unchanged
		return { available: true, unread: answer.unread, more: answer.more === true, sources: answer.sources ?? [] };
	}

	async list(request: NoticeRequest): Promise<NoticePage> {
		if (this.#availability.held(false)) return { available: false, items: [], reason: this.reason };
		const query = this.#query({ state: request.state, product: request.product ?? undefined, cursor: typeof request.cursor === 'string' ? request.cursor : undefined, limit: String(request.limit) });
		const answer = await this.#call<{ items: IItem[]; next?: string; sources?: ISource[] }>('GET', `/v1/notifications?${query}`);
		if (!answer) return { available: false, items: [] };
		if (this.#availability.note(answer)) return { available: false, items: [], reason: this.reason };
		const items = answer.items.map(item => ({ id: item.id, product: item.product, kind: item.kind, title: item.title, summary: item.summary ?? null, occurred: item.occurred, read: item.read ?? null, group: item.group ?? null }));
		return { available: true, items, next: answer.next ?? null, sources: answer.sources ?? [] };
	}

	read(target: string[] | { before: string; product?: string }): Promise<unknown> {
		return this.#api.relay('POST', '/v1/notifications/read', Array.isArray(target) ? { ids: target } : target);
	}

	unread(ids: string[]): Promise<unknown> {
		return this.#api.relay('POST', '/v1/notifications/unread', { ids });
	}

	/**
	 * Projects asks the producer again, marks the item read and answers where it leads. `NOT_FOUND` (denied,
	 * gone or expired) reaches the components as that code, and they remove the item.
	 */
	async open(id: string): Promise<{ destination: string | null }> {
		const answer = (await this.#api.relay('POST', `/v1/notifications/${encodeURIComponent(id)}/open?${this.#query({})}`, {})) as { destination?: string };
		return { destination: typeof answer?.destination === 'string' ? answer.destination : null };
	}

	// An unavailable inbox resolves as nothing; any other refusal is a failure the components offer to retry
	async #call<T>(method: 'GET' | 'POST', path: string): Promise<T | undefined> {
		try {
			return (await this.#api.relay(method, path)) as T;
		} catch (thrown) {
			const problem = ApiProblem.of(thrown);
			if (problem.is('UNAVAILABLE') || problem.status === 503 || problem.status === 0) return undefined;
			throw problem;
		}
	}

	#query(values: Record<string, string | undefined>): string {
		const search = new URLSearchParams();
		for (const [name, value] of Object.entries({ ...values, locale: this.#locale() })) if (value) search.set(name, value);
		return search.toString();
	}
}
